import { useState, useEffect } from "react";
import { Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CartDrawer } from "@/components/CartDrawer";
import { SearchDialog } from "@/components/SearchDialog";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { clearAdminToken, getAdminToken } from "@/api/client";
import { ASSETS } from "@/lib/assets";

const logo = ASSETS.logo;
const logoIcon = ASSETS.faviconPng;

export const Header = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    setIsAdmin(Boolean(getAdminToken()));
  }, []);

  // Ctrl/Cmd + K opens the search palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    clearAdminToken();
    setIsAdmin(false);
    window.location.href = "/";
  };
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-20 items-center justify-between">
        <div className="flex items-center gap-2">
          <a href="/" className="flex items-center shrink-0" aria-label="Biz Certificadora Digital — Início">
            {/* Icon only on mobile */}
            <img
              src={logoIcon}
              alt="Biz Certificadora Digital"
              className="h-10 w-10 object-contain sm:hidden"
              width={40}
              height={40}
            />
            {/* Full logotype on sm+ */}
            <img
              src={logo}
              alt="Biz Certificadora Digital"
              className="hidden h-14 w-auto max-w-[220px] object-contain sm:block"
            />
          </a>
        </div>
        
        <nav className="hidden md:flex items-center gap-6">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium text-foreground/80 hover:text-foreground">
                  Certificados
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[640px] grid-cols-3 gap-1 p-4 bg-background">
                    {/* Coluna 1 — Por tipo (conversão direta) */}
                    <div className="space-y-1">
                      <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                        Por tipo
                      </p>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-pf"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">e-CPF</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Pessoa Física · IR, Gov.br, assinatura
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-pj"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">e-CNPJ</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Pessoa Jurídica · NF-e, eSocial
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-mei"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">MEI</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Microempreendedor · fast-track 30min
                          </p>
                        </a>
                      </NavigationMenuLink>
                    </div>

                    {/* Coluna 2 — Guias */}
                    <div className="space-y-1">
                      <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                        Guias e ajuda
                      </p>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-digital"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Guia completo ICP-Brasil</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Tudo sobre certificado digital
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-digital/a1-ou-a3"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">A1 ou A3</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Comparativo completo
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/renovar-certificado-digital"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Renovar certificado</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Antes que vença
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-digital/migrar-serasa"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Migrar da Serasa</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Transição sem perda
                          </p>
                        </a>
                      </NavigationMenuLink>
                    </div>

                    {/* Coluna 3 — Por uso / Profissão */}
                    <div className="space-y-1">
                      <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                        Por uso
                      </p>
                      <NavigationMenuLink asChild>
                        <a
                          href="/profissoes"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Por profissão</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            OAB, CRM, CREA, CRC e mais
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-digital/nfe"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Para NF-e</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Emissores e ERPs
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-digital/esocial"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Para eSocial</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            Obrigações trabalhistas
                          </p>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/certificado-digital/contabilidade"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <div className="text-sm font-semibold leading-none">Para contador</div>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                            CRC · SPED · e-CAC
                          </p>
                        </a>
                      </NavigationMenuLink>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          <a href="/blog" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
            Blog
          </a>

          {isAdmin && (
            <a href="/admin/dashboard" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
              Dashboard
            </a>
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Search trigger — minimal button that opens command palette */}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Buscar"
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Buscar no site…</span>
            <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[9px] font-semibold">
              ⌘K
            </kbd>
          </button>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Buscar"
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted/60"
          >
            <Search className="h-4 w-4 text-foreground" />
          </button>

          <CartDrawer />

          {isAdmin ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Painel">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Painel</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/admin/dashboard">Dashboard Admin</a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" asChild className="gap-2">
              <a href="/cliente/login">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Minha conta</span>
              </a>
            </Button>
          )}
        </div>
      </div>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
